import { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAxiosInstance } from "../../../../services/Interceptor";
import {
  deletePositionDrillAction,
  savePositionDrillCoachingTipAction,
} from "../../../../store/CombineActions";
import { toast } from "react-toastify";
import ConfirmModal from "../../../../components/ConfirmModel";
import CoachingTipsModal from "../../../../components/CoachingTipsModal";
import ReactPlayer from "react-player";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

const emptyTipsState = {
  isOpen: false,
  positionDrillId: null,
  initialCoachingTip: "",
};

const hasCoachingTip = (coachingTip) => Boolean(coachingTip?.trim());

export default function PositionDrillList() {
  const dispatch = useDispatch();
  const r_combine = useSelector((state) => state.combine);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [tipsState, setTipsState] = useState(emptyTipsState);
  const [coachingTip, setCoachingTip] = useState("");
  const [coachingTipError, setCoachingTipError] = useState("");
  const [savingCoachingTip, setSavingCoachingTip] = useState(false);
  const apiInstance = useAxiosInstance();

  const list = useMemo(
    () =>
      r_combine.positionDrill.reduce((acc, video) => {
        const { positionName, drillName, rep } = video;
        if (!acc[positionName]) {
          acc[positionName] = {};
        }
        if (!acc[positionName][drillName]) {
          acc[positionName][drillName] = [];
        }
        if (!acc[positionName][drillName][rep]) {
          acc[positionName][drillName][rep] = [];
        }
        acc[positionName][drillName][rep].push(video);
        return acc;
      }, {}),
    [r_combine.positionDrill],
  );

  const handleDeleteVideo = (positionDrillId) => {
    setVideoToDelete(positionDrillId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deletePositionDrillAction(apiInstance, videoToDelete));
      toast.success("Video deleted successfully!", {
        position: "top-right",
      });
      setShowDeleteModal(false);
      setVideoToDelete(null);
    } catch (error) {
      toast.error("Failed to delete video. Please try again.", {
        position: "top-right",
      });
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setVideoToDelete(null);
  };

  const openTipsModal = (item) => {
    const initialCoachingTip = item.coachingTip || "";
    setTipsState({
      isOpen: true,
      positionDrillId: item.id,
      initialCoachingTip,
    });
    setCoachingTip(item.coachingTip || "");
    setCoachingTipError("");
  };

  const closeTipsModal = () => {
    if (savingCoachingTip) {
      return;
    }

    setTipsState(emptyTipsState);
    setCoachingTip("");
    setCoachingTipError("");
  };

  const handleSaveCoachingTip = async (event) => {
    event.preventDefault();

    const trimmedCoachingTip = coachingTip.trim();

    if (!tipsState.initialCoachingTip && !trimmedCoachingTip) {
      closeTipsModal();
      return;
    }

    if (!tipsState.positionDrillId) {
      toast.error("Unable to save tips. Please try again.", {
        position: "top-right",
      });
      return;
    }

    try {
      setSavingCoachingTip(true);
      await dispatch(
        savePositionDrillCoachingTipAction(
          apiInstance,
          tipsState.positionDrillId,
          trimmedCoachingTip,
        ),
      );
      toast.success(
        trimmedCoachingTip
          ? "Tips saved successfully!"
          : "Tips deleted successfully!",
        { position: "top-right" },
      );
      setTipsState(emptyTipsState);
      setCoachingTip("");
      setCoachingTipError("");
    } catch (error) {
      toast.error("Failed to save tips. Please try again.", {
        position: "top-right",
      });
    } finally {
      setSavingCoachingTip(false);
    }
  };

  return (
    <>
      {Object.entries(list).length > 0 ? (
        <div>
          {Object.entries(list).map(([positionName, drills]) => (
            <div key={positionName}>
              <span className="bg-gray-200 font-bold text-gray-700  block p-1">
                {positionName}
              </span>
              {Object.entries(drills).map(([drillName, reps]) => (
                <div key={`${positionName}-${drillName}`}>
                  <span className="font-bold text-gray-700  bg-gray-100 block p-1">
                    {drillName}
                  </span>
                  {Object.entries(reps).map(([repNumber, videos]) => (
                    <div key={`${positionName}-${drillName}-${repNumber}`}>
                      <span className="text-gray-700 block p-1 mt-2">
                        Rep {repNumber}
                      </span>
                      <div className="bg-white p-0 grid grid-cols-3 gap-3 mb-4">
                        {videos.map((item, key) => {
                          const hasTips = hasCoachingTip(item.coachingTip);

                          return (
                            <div
                              key={item.id ?? key}
                              className="flex flex-col items-center relative"
                            >
                              <button
                                type="button"
                                onClick={() => openTipsModal(item)}
                                className="absolute -top-7 right-0 flex items-center text-sm text-gray-600 hover:text-blue-800 hover:underline"
                              >
                                <img
                                  className="h-4 pr-2"
                                  alt={hasTips ? "View Tips" : "Add Tips"}
                                  src={hasTips ? "/viewnote.svg" : "/note.svg"}
                                />
                                {hasTips ? "Edit Tips" : "Add Tips"}
                              </button>
                              <div className="w-full h-[150px] bg-black rounded-md">
                                <ReactPlayer
                                  controls
                                  light={true}
                                  src={item.filePath}
                                  playsInline={true}
                                  height={150}
                                  width="100%"
                                  className="rounded-md"
                                />
                              </div>
                              <div className="flex justify-between items-center w-full mt-1 p-1">
                                <span className="text-gray-700 truncate w-10/12">
                                  {" "}
                                  {item.angle}{" "}
                                </span>{" "}
                                <span>
                                  <FontAwesomeIcon
                                    onClick={() => handleDeleteVideo(item.id)}
                                    icon={faTrash}
                                    size="lg"
                                    className="text-red-500 cursor-pointer hover:text-red-700 text-sm"
                                  />
                                </span>{" "}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-blue-100 text-blue-800 mt-5 p-2 border border-blue-300 w-full">
          There are no drills added
        </div>
      )}
      <CoachingTipsModal
        isOpen={tipsState.isOpen}
        isEditing={hasCoachingTip(tipsState.initialCoachingTip)}
        coachingTip={coachingTip}
        coachingTipError={coachingTipError}
        isSaving={savingCoachingTip}
        onClose={closeTipsModal}
        onChange={(nextCoachingTip) => {
          setCoachingTip(nextCoachingTip);
          if (coachingTipError) {
            setCoachingTipError("");
          }
        }}
        onSubmit={handleSaveCoachingTip}
      />
      {/* Render ConfirmModal for deletion */}
      <ConfirmModal
        show={showDeleteModal}
        message="Are you sure you want to delete this video?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
}
